import type { ConsentStatus } from "@/api/generated";

/**
 * PRIV-1 consent decision logic + per-provider disclosure copy.
 *
 * Pure and framework-free so it's unit-testable; ChatPage wires it to the
 * `fetchConsent`/`recordConsent` API and the `ConsentModal` component.
 */

/** Stable provider identifiers — must match the backend's `tool_consents`. */
export type EgressProvider = "exa" | "gemini" | "tinyfish" | "tinyfish_agent";

/** What the consent modal shows for each provider. */
export interface ProviderDisclosure {
  /** Modal heading, framed around the action the user just took. */
  readonly title: string;
  /** Plain-language statement of exactly what data is sent and to whom. */
  readonly body: string;
  /** Affirmative button label. */
  readonly allowLabel: string;
}

export const PROVIDER_DISCLOSURES: Record<EgressProvider, ProviderDisclosure> = {
  exa: {
    title: "Before using Web Search",
    body: "This sends your search query to Exa (exa.ai), a third-party search provider. Your conversation and uploaded files are not sent.",
    allowLabel: "Allow Exa search",
  },
  tinyfish: {
    title: "Before using TinyFish",
    body: "This sends your search query to TinyFish (tinyfish.ai), a third-party search provider, which also fetches the top result pages on your behalf. Your conversation and uploaded files are not sent.",
    allowLabel: "Allow TinyFish",
  },
  tinyfish_agent: {
    title: "Before using TinyFish Agent",
    body: "This sends the URL and your goal to TinyFish (tinyfish.ai). TinyFish will open a real browser, click, type, and extract on that site. Your conversation and uploaded files are not sent. Do not use this to log in, spend money, or send messages.",
    allowLabel: "Allow TinyFish Agent",
  },
  gemini: {
    title: "Before generating an image",
    body: "This sends your image prompt and any image already in this conversation to Google (Gemini), a third-party image provider.",
    allowLabel: "Allow Gemini image",
  },
};

/** Disclosure for a provider, or `undefined` for an unknown id. */
export function providerDisclosure(
  provider: string,
): ProviderDisclosure | undefined {
  return PROVIDER_DISCLOSURES[provider as EgressProvider];
}

/**
 * Whether the consent modal must be shown before using `provider`.
 *
 * Only when consent is enforced server-side AND the user hasn't already
 * consented. When status is still loading (`undefined`) we don't block — the
 * backend remains the authoritative gate and will 403 if a record is truly
 * required, so we never silently send data the user hasn't agreed to.
 */
export function consentNeeded(
  status: ConsentStatus | undefined,
  provider: string,
): boolean {
  if (!status || !status.enforced) return false;
  return !status.consented.includes(provider);
}
